import axios from 'axios'
import Cookies from 'cookies-js'
import { Dispatch } from 'react'

import { formatFlags, SUCCESS } from '~/redux-modules/identity'
import { RESELLER } from '~/redux-modules/preferences'
import { trackIdentify } from '~/lib/analytics/actions/identify'
import { MUNCHKIN_HASH_COOKIE } from '~/lib/analytics/handlers/munchkin'
import { isTokenExpired } from '~/lib/jwtHelper'
import { setUserContext } from '~/sentry'
import { getenv } from '~/settings'
import {
    ROLE_IDS,
    ID_PROJECT_ADMIN,
    ID_ENTERPRISE,
    ID_BUSINESS_ADMIN,
    ID_COSTING_AND_DFM,
    ID_DFM_MESSAGE_EDITOR,
    ID_SUPPLIER_QUALITY_ENGINEER,
    ID_SCHEDULING_ADMIN,
    ASSIGN_MP,
    ID_QUALITY_MANAGER,
} from '~/apps/consumer/pages/Quotes/Admin/AdminUserProfilePage/constants'
import {
    CurrentUserIdentityDocument,
    User,
    UserPreferences,
} from '~/gql/graphql'

import { AuthActionType, AuthResponseType, AuthStateType } from './types'

export const GRAPHQL_API_REST_BASE = getenv('GRAPHQL_API_REST_BASE')

export const FICTIV_JWT_COOKIE = '_fictiv_jwt'
export const SET_AUTH_SUCCESS = 'set-auth-success'
export const RESET_AUTH_SUCCESS = 'reset-auth-success'
export const SET_AUTH_ERROR = 'set-auth-error'
export const SET_MFA_STATUS = 'set-mfa-status'
export const SET_USER_VIEW_PREFERENCES = 'set-user-view-preferences'

export type AuthUserIdentityTypeWithMfa = User & {
    mfaEnabled: boolean
}

const initialState = {
    idToken: null,
    user: {},
    error: null,
}

export default (state = initialState, action: AuthActionType) => {
    const {
        idToken,
        user,
        error = null,
        mfaEnabled = false,
        userPreferences,
    } = action

    switch (action.type) {
        case SET_AUTH_SUCCESS:
            return {
                ...state,
                idToken,
                user,
                error,
            }
        case RESET_AUTH_SUCCESS:
            return {
                ...state,
                idToken,
                user,
                error,
            }
        case SET_AUTH_ERROR:
            return {
                ...state,
                idToken,
                user,
                error,
            }
        case SET_MFA_STATUS:
            // eslint-disable-next-line no-case-declarations
            const newUser = { ...user, mfaEnabled }

            return {
                ...state,
                user: newUser,
            }
        case SET_USER_VIEW_PREFERENCES:
            return {
                ...state,
                idToken,
                user: { ...state.user, userPreferences },
                error,
            }
        default:
            return state
    }
}

export const getAuthIdToken = (state: AuthStateType) => {
    return state.auth.idToken || Cookies.get(FICTIV_JWT_COOKIE)
}

export const getMfaStatus = (state: AuthStateType) =>
    state.auth.user?.mfaEnabled

export const getUserViewPreferences = (state: AuthStateType) =>
    state.auth.user?.userPreferences

export const formatAuthResponse = (
    response: { me: AuthUserIdentityTypeWithMfa },
    idToken: string | null | undefined,
) => {
    const {
        id,
        createdAt,
        displayName,
        email,
        emailVerificationStatus,
        emailVerified,
        emailVerifiedDate,
        firstName,
        flags,
        lastName,
        quotePartLimit,
        roles = [],
        zipcode,
        mfaEnabled,
        businessAccountMembership,
        userPreferences,
        teams,
    } = response.me

    const { ID_EMPLOYEE, ID_MP } = ROLE_IDS

    const isAdmin = roles.some(role => role.name === ID_EMPLOYEE)
    const isSupplierQualityEngineer = roles.some(
        role => role.name === ID_SUPPLIER_QUALITY_ENGINEER,
    )
    const isVendor = roles.some(role => role.name === ID_MP)
    const isReseller = roles.some(role => role.name === RESELLER)
    const isProjectAdmin = roles.some(role => role.name === ID_PROJECT_ADMIN)
    const isEnterprise = roles.some(role => role.name === ID_ENTERPRISE)
    const isBusinessAdmin = roles.some(role => role.name === ID_BUSINESS_ADMIN)
    const isCostingAndDfm = roles.some(role => role.name === ID_COSTING_AND_DFM)
    const munchkinHash = Cookies.get(MUNCHKIN_HASH_COOKIE) || null
    const isDfmFeedbackEditor = roles.some(
        role => role.name === ID_DFM_MESSAGE_EDITOR,
    )
    const isSourcingManager = roles.some(role => role.name === ASSIGN_MP)
    const isQualityManager = roles.some(
        role => role.name === ID_QUALITY_MANAGER,
    )
    const isSchedulingAdmin = roles.some(
        role => role.name === ID_SCHEDULING_ADMIN,
    )

    return {
        id,
        createdAt,
        discounts: [],
        email,
        emailVerificationStatus,
        emailVerified,
        emailVerifiedDate,
        firstName,
        isAdmin,
        isSupplierQualityEngineer,
        isBusinessAdmin,
        isCostingAndDfm,
        isVendor,
        isProjectAdmin,
        isReseller,
        isEnterprise,
        isSchedulingAdmin,
        lastName,
        loading: true,
        munchkinHash,
        name: displayName,
        postalCode: zipcode,
        token: idToken,
        trueUser: undefined, // Don't need anymore i think, tbd
        isImpersonating: false,
        mfaEnabled,
        quotePartLimit,
        userPreferences,
        // NOTE: temporary version of "flags" that will
        //       eventually be calculated on the backend
        //       and passed through.
        flags: formatFlags(flags),

        businessAccountMembership,
        teams,
        isDfmFeedbackEditor,
        isSourcingManager,
        isQualityManager,
    }
}

export const refreshToken = () => {
    return async (
        dispatch: Dispatch<AuthActionType>,
        getState: () => any,
        { client }: any,
    ) => {
        const state = getState()
        let _error = 'No tokens found in redux or in cookies'
        const storedToken = getAuthIdToken(state)

        if (storedToken && !isTokenExpired(storedToken)) {
            dispatch({ type: SET_AUTH_SUCCESS, idToken: storedToken })

            const userIdentityResponse = await client.query({
                query: CurrentUserIdentityDocument,
            })

            if (userIdentityResponse?.data?.error) {
                throw userIdentityResponse.data.error
            }

            const { data: userIdentityData } = userIdentityResponse
            const identity = formatAuthResponse(userIdentityData, storedToken)

            const { email, id } = identity
            const action = { type: SUCCESS, identity }

            dispatch(trackIdentify(action, id, identity))
            setUserContext({ email, id })

            return storedToken
        } else if (storedToken) {
            const {
                data: { idToken, error },
            } = await axios.post(
                `${GRAPHQL_API_REST_BASE}/refreshToken`,
                {},
                { withCredentials: true },
            )

            if (!error) {
                dispatch({ type: SET_AUTH_SUCCESS, idToken })

                return idToken
            }

            _error = error
        }

        // NOTE: This should not be an error but the app does not get redirected to
        // login correctly without it. refreshToken and its use needs to be refactored.
        dispatch({ type: SET_AUTH_ERROR, error: _error })
        throw _error
    }
}

export const setAuth = (
    response: AuthResponseType,
    error = null,
): AuthActionType => {
    const {
        user,
        auth: { idToken },
    } = response

    return {
        type: SET_AUTH_SUCCESS,
        idToken,
        user,
        error,
    }
}

export const resetAuth = () => {
    return {
        type: RESET_AUTH_SUCCESS,
        idToken: null,
        user: {},
        error: null,
    }
}

export const setAuthError = (error: string) => ({
    type: SET_AUTH_ERROR,
    error,
})

export const setMfaStatus = (mfaEnabled: boolean) => ({
    type: SET_MFA_STATUS,
    mfaEnabled,
})

export const setUserPreferences = (
    userPreferences: Partial<UserPreferences>,
) => ({
    type: SET_USER_VIEW_PREFERENCES,
    userPreferences,
})
